import React, { useMemo, useState, useCallback } from "react";
import {
  useTable,
  useSortBy,
  useExpanded,
  useColumnOrder,
  usePagination,
  useRowSelect,
  useGroupBy,
} from "react-table";
import PropTypes from "prop-types";
import TableHeader from "./TableHeader";
import TableBody from "./TableBody";
import AddNewItemRow from "./AddNewItemRow";
import SubTotalRow from "./SubTotalRow";
import { SendMessage } from "../../components/SendMessage";
import "./index.css";
import ErrorRow from "./ErrorRow";
import {
  KeyboardArrowRight,
  KeyboardArrowDown,
  Assessment,
  CloudUpload,
} from "@mui/icons-material";
import { useExcelExport } from "../../hooks/useExcelExport";
import EFRAGService from "../../apis/EFRAG/efragService";
import exportIcon from "../../assets/design-revamp-company/export-excel/export_icon.svg";

const CustomTable = React.memo(
  ({
    columns,
    data,
    setData,
    onAdd,
    onEdit,
    onDelete,
    subtotalColumns,
    customInputs,
    dropdownData,
    hiddenColumns,
    toggleColumnVisibility,
    disableRules,
    dynamicPlaceholders,
    noAddNewRow = false,
    initialPageSize = 10,
    pageSizeOptions = [5, 10, 20, 30, 40, 50],
    enableGrouping = false,
    enablePagination = false,
    groupBy = [],
    noShow,
    enableExport = true,
    enableEFRAGIntegration = false,
    reportMetadata = {},
  }) => {
    const [errors, setErrors] = useState({});
    const [editingRow, setEditingRow] = useState(null);
    const [editedData, setEditedData] = useState({});
    const [open, setOpen] = useState(false);
    const [newRows, setNewRows] = useState([]);
    const [efragStatus, setEfragStatus] = useState(null);
    const [isExporting, setIsExporting] = useState(false);
    const { exportToExcel, generateEFRAGTemplate } = useExcelExport();

    const tableColumns = useMemo(() => {
      const baseColumns = [
        ...columns.map((col) => ({
          ...col,
          isVisible: !hiddenColumns.includes(col.accessor),
          aggregate: col.type === "number" ? "sum" : "unique",
          Aggregated: ({ value }) => {
            if (typeof value === "number") {
              return `Total: ${value.toFixed(2)}`;
            }
            return `${value}`;
          },
        })),
      ];

      // Only add expander column if grouping is enabled
      if (enableGrouping) {
        return [
          {
            id: "expander",
            Header: ({ getToggleAllRowsExpandedProps, isAllRowsExpanded }) => (
              <span {...getToggleAllRowsExpandedProps()}>
                {isAllRowsExpanded ? (
                  <KeyboardArrowDown className="expand-icon" />
                ) : (
                  <KeyboardArrowDown className="expand-icon" />
                )}
              </span>
            ),
            Cell: ({ row }) =>
              row.canExpand ? (
                <span
                  {...row.getToggleRowExpandedProps({
                    style: {
                      paddingLeft: `${row.depth * 2}rem`,
                    },
                  })}
                >
                  {row.isExpanded ? (
                    <KeyboardArrowDown className="expand-icon" />
                  ) : (
                    <KeyboardArrowRight className="expand-icon" />
                  )}
                </span>
              ) : null,
            width: 50,
          },
          ...baseColumns,
          {
            id: "actions",
            header: "",
            Cell: ({ row }) => null,
            width: 80,
          },
        ];
      }

      return [
        ...baseColumns,
        {
          id: "actions",
          header: "",
          Cell: ({ row }) => null,
          width: 80,
        },
      ];
    }, [columns, hiddenColumns, enableGrouping]);

    const fullTableData = useMemo(() => [...data, ...newRows], [data, newRows]);

    const tableInstance = useTable(
      {
        columns: tableColumns,
        data: fullTableData,
        initialState: {
          pageSize: initialPageSize,
          ...(enableGrouping && { groupBy: groupBy }),
        },
      },
      useColumnOrder,
      ...(enableGrouping ? [useGroupBy] : []),
      useSortBy,
      useExpanded,
      ...(enablePagination ? [usePagination] : []),
      useRowSelect
    );

    const {
      getTableProps,
      getTableBodyProps,
      headerGroups,
      page,
      rows,
      prepareRow,
      canPreviousPage,
      canNextPage,
      pageOptions,
      pageCount,
      gotoPage,
      nextPage,
      previousPage,
      setPageSize,
      state: { pageIndex, pageSize },
      toggleGroupBy,
      toggleAllRowsExpanded,
      // allColumns,
    } = tableInstance;

    const rowsToRender = enablePagination ? page : rows;

    // Export functions
    const handleExportToExcel = useCallback(async () => {
      try {
        setIsExporting(true);
        const filename = `${reportMetadata.entityName || "table"}-export-${
          new Date().toISOString().split("T")[0]
        }`;
        await exportToExcel(fullTableData, columns, filename);
      } catch (error) {
        console.error("Export failed:", error);
        alert("Export failed. Please try again.");
      } finally {
        setIsExporting(false);
      }
    }, [fullTableData, columns, exportToExcel, reportMetadata]);

    const handleEFRAGExport = useCallback(async () => {
      try {
        setIsExporting(true);
        const efragBlob = await generateEFRAGTemplate(fullTableData, columns);
        const filename = `${reportMetadata.entityName || "efrag"}-template-${
          new Date().toISOString().split("T")[0]
        }.xlsx`;

        // Download the EFRAG template
        const url = window.URL.createObjectURL(efragBlob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        if (enableEFRAGIntegration) {
          // Upload to EFRAG converter
          const uploadResult = await EFRAGService.uploadToEFRAGConverter(
            efragBlob
          );
          setEfragStatus({ status: "uploaded", fileId: uploadResult.fileId });
        }
      } catch (error) {
        console.error("EFRAG export failed:", error);
        alert("EFRAG export failed. Please try again.");
      } finally {
        setIsExporting(false);
      }
    }, [
      fullTableData,
      columns,
      generateEFRAGTemplate,
      reportMetadata,
      enableEFRAGIntegration,
    ]);

    const handleEFRAGConversion = useCallback(async () => {
      if (!efragStatus?.fileId) return;

      try {
        setIsExporting(true);
        const conversionResult = await EFRAGService.processEFRAGConversion(
          efragStatus.fileId
        );
        setEfragStatus({
          ...efragStatus,
          status: "converting",
          conversionId: conversionResult.conversionId,
        });

        // Poll for conversion status
        const checkStatus = async () => {
          const status = await EFRAGService.getConversionStatus(
            conversionResult.conversionId
          );
          setEfragStatus((prev) => ({ ...prev, ...status }));

          if (status.status === "completed") {
            const reportBlob = await EFRAGService.downloadConvertedReport(
              conversionResult.conversionId
            );
            const url = window.URL.createObjectURL(reportBlob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `efrag-report-${
              new Date().toISOString().split("T")[0]
            }.xbrl`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
          } else if (status.status === "failed") {
            alert(
              "EFRAG conversion failed. Please check your data and try again."
            );
          } else {
            setTimeout(checkStatus, 2000); // Poll every 2 seconds
          }
        };

        setTimeout(checkStatus, 1000);
      } catch (error) {
        console.error("EFRAG conversion failed:", error);
        alert("EFRAG conversion failed. Please try again.");
      } finally {
        setIsExporting(false);
      }
    }, [efragStatus]);

    const renderGroupHeader = useCallback((row) => {
      const groupValue = row.values[row.groupByID];
      return (
        <span>
          {row.groupByID}: {groupValue} ({row.subRows.length} items)
        </span>
      );
    }, []);

    const handleAddNewItem = useCallback((newItem) => {
      setNewRows((prev) => [...prev, newItem]);
    }, []);

    const handleInputChange = useCallback((rowIndex, columnId, value) => {
      setEditedData((prev) => ({
        ...prev,
        [rowIndex]: {
          ...prev[rowIndex],
          [columnId]: value,
        },
      }));
    }, []);

    const handleValidation = useCallback(
      (newData) => {
        const newErrors = {};
        columns.forEach((column) => {
          const { accessor, type, optional, errorText } = column;
          const value = newData[accessor];

          if (!optional && !value) {
            newErrors[accessor] = errorText || "This field is required";
          }
          if (value) {
            if (type === "number" && isNaN(Number(value))) {
              newErrors[accessor] = errorText || "Invalid number";
            } else if (type === "text" && typeof value !== "string") {
              newErrors[accessor] = errorText || "Invalid text";
            }
          }
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
      },
      [columns]
    );

    const handleSave = useCallback(
      (rowIndex) => {
        const newData = editedData[rowIndex] || {};
        const newErrors = handleValidation(newData);

        if (Object.keys(newErrors).length === 0) {
          const row = rows[rowIndex];
          const isNewRow = row.index >= data.length;
          const rowData = { ...row.original, ...newData };

          if (isNewRow) {
            setNewRows((prev) =>
              prev.map((r, i) => (i === row.index - data.length ? rowData : r))
            );
            onAdd(rowData);
          } else {
            setData((prev) =>
              prev.map((r, i) => (i === row.index ? rowData : r))
            );
            onEdit(rowData);
          }

          setEditedData((prev) => {
            const { [rowIndex]: removed, ...rest } = prev;
            return rest;
          });
          setEditingRow(null);
          setErrors({});
        } else {
          setErrors(newErrors);
        }
      },
      [editedData, rows, data.length, onAdd, onEdit, setData, handleValidation]
    );

    const handleDelete = useCallback(
      (rowIndex) => {
        return onDelete(rowIndex);
      },
      [onDelete]
    );

    const handleStartEditing = useCallback(
      (rowIndex) => {
        setEditingRow(rowIndex);
        setEditedData((prev) => ({
          ...prev,
          [rowIndex]: { ...data[rowIndex] },
        }));
      },
      [data]
    );

    const handleCancelEdit = useCallback((rowIndex) => {
      setEditingRow(null);
      setEditedData((prev) => {
        const newData = { ...prev };
        delete newData[rowIndex];
        return newData;
      });
    }, []);

    const renderExportToolbar = () => (
      <div className="export-toolbar">
        {enableExport && (
          <button
            onClick={handleExportToExcel}
            disabled={isExporting}
            className="export-button excel-export"
          >
            <img src={exportIcon} alt="Export icon" width={18} height={18} />
            {isExporting ? "Exporting..." : "Export to Excel"}
          </button>
        )}

        {enableEFRAGIntegration && (
          <>
            <button
              onClick={handleEFRAGExport}
              disabled={isExporting}
              className="export-button efrag-export"
            >
              <Assessment className="export-icon" />
              {isExporting ? "Generating..." : "Generate EFRAG Template"}
            </button>

            {efragStatus?.fileId && (
              <button
                onClick={handleEFRAGConversion}
                disabled={isExporting || efragStatus.status === "converting"}
                className="export-button efrag-convert"
              >
                <CloudUpload className="export-icon" />
                {efragStatus.status === "converting"
                  ? "Converting..."
                  : "Convert to XBRL"}
              </button>
            )}
          </>
        )}
      </div>
    );

    const renderPagination = () => (
      <div className="pagination-container">
        <div className="pagination-buttons">
          <button
            onClick={() => gotoPage(0)}
            disabled={!canPreviousPage}
            className="pagination-button"
          >
            {"<<"}
          </button>
          <button
            onClick={() => previousPage()}
            disabled={!canPreviousPage}
            className="pagination-button"
          >
            {"<"}
          </button>
          <button
            onClick={() => nextPage()}
            disabled={!canNextPage}
            className="pagination-button"
          >
            {">"}
          </button>
          <button
            onClick={() => gotoPage(pageCount - 1)}
            disabled={!canNextPage}
            className="pagination-button"
          >
            {">>"}
          </button>
        </div>
        <span className="pagination-info">
          Page{" "}
          <strong>
            {pageIndex + 1} of {pageOptions.length}
          </strong>
        </span>
        <select
          value={pageSize}
          onChange={(e) => setPageSize(Number(e.target.value))}
          className="pagination-select"
        >
          {pageSizeOptions.map((size) => (
            <option key={size} value={size}>
              Show {size}
            </option>
          ))}
        </select>
      </div>
    );
    return (
      <div className="custom-table">
        {(enableExport || enableEFRAGIntegration) && renderExportToolbar()}

        {efragStatus && (
          <div className="efrag-status">
            Status: {efragStatus.status}
            {efragStatus.message && ` - ${efragStatus.message}`}
          </div>
        )}
        <table
          {...getTableProps()}
          style={{ tableLayout: "fixed", width: "100%" }}
        >
          <TableHeader
            headerGroups={headerGroups}
            toggleColumnVisibility={toggleColumnVisibility}
            hiddenColumns={hiddenColumns}
            toggleGroupBy={enableGrouping ? toggleGroupBy : undefined}
          />
          <TableBody
            getTableBodyProps={getTableBodyProps}
            rows={rowsToRender}
            prepareRow={prepareRow}
            customInputs={customInputs}
            dropdownData={dropdownData}
            onInputChange={handleInputChange}
            hiddenColumns={hiddenColumns}
            editingRow={editingRow}
            editedData={editedData}
            onStartEditing={handleStartEditing}
            onSave={handleSave}
            onCancelEdit={handleCancelEdit}
            onDelete={handleDelete}
            setOpen={setOpen}
            disableRules={disableRules}
            noShow={noShow}
            dynamicPlaceholders={dynamicPlaceholders}
            renderGroupHeader={enableGrouping ? renderGroupHeader : undefined}
          />
          {!noAddNewRow &&
            newRows.map((_, index) => (
              <AddNewItemRow
                key={index}
                columns={columns}
                onAdd={onAdd}
                onAddRow={handleAddNewItem}
                customInputs={customInputs}
                dropdownData={dropdownData}
                hiddenColumns={hiddenColumns}
                handleValidation={handleValidation}
                disableRules={disableRules}
                noShow={noShow}
                dynamicPlaceholders={dynamicPlaceholders}
                newRows={newRows}
                setOpen={setOpen}
              />
            ))}
          {!noAddNewRow && (
            <AddNewItemRow
              columns={columns}
              onAdd={onAdd}
              onAddRow={handleAddNewItem}
              customInputs={customInputs}
              dropdownData={dropdownData}
              hiddenColumns={hiddenColumns}
              handleValidation={handleValidation}
              disableRules={disableRules}
              noShow={noShow}
              dynamicPlaceholders={dynamicPlaceholders}
              setOpen={setOpen}
            />
          )}
          {!noAddNewRow && <ErrorRow columns={columns} errors={errors} />}
          {!noAddNewRow && (
            <SubTotalRow
              columns={columns}
              subtotalColumns={subtotalColumns}
              data={fullTableData}
              hiddenColumns={hiddenColumns}
            />
          )}
        </table>
        {enablePagination && renderPagination()}
        {open && (
          <div
            className="modal-overlay"
            style={{ zIndex: 999999, backgroundColor: "#FFFFFF" }}
          >
            <SendMessage setOpen={setOpen} />
          </div>
        )}
      </div>
    );
  }
);

CustomTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      accessor: PropTypes.string.isRequired,
      header: PropTypes.string.isRequired,
      type: PropTypes.oneOf([
        "text",
        "number",
        "dropdown",
        "switch",
        "custom",
        "spacer",
        "function",
        "multipleCustom",
      ]).isRequired,
      optional: PropTypes.bool,
      canSort: PropTypes.bool,
      choice1: PropTypes.string,
      choice2: PropTypes.string,
      styleNo: PropTypes.number,
      canHide: PropTypes.bool,
      tipHeader: PropTypes.string,
      tipBody: PropTypes.string,
      width: PropTypes.string,
      subtotalFixed: PropTypes.bool,
      subtotalUnit: PropTypes.string,
      unit: PropTypes.string,
      minValue: PropTypes.number,
      maxValue: PropTypes.number,
    })
  ).isRequired,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  onAdd: PropTypes.func,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  subtotalColumns: PropTypes.objectOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
    })
  ),
  customInputs: PropTypes.objectOf(PropTypes.func),
  dropdownData: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.object)),
  initialPageSize: PropTypes.number,
  pageSizeOptions: PropTypes.arrayOf(PropTypes.number),
  groupBy: PropTypes.arrayOf(PropTypes.string),
  enableExport: PropTypes.bool,
  enableEFRAGIntegration: PropTypes.bool,
  reportMetadata: PropTypes.object,
};

export default CustomTable;
